import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, Plus, X } from 'lucide-react';
import { FileTextIcon } from '@hero/web-icons';
import { useAgreementListApi } from '@hero/api-hooks';
import { useCurrentUser } from '@hero/hooks-shared';
import type { Agreement } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { Button } from '../../../ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/Popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '../../../ui/Command';
import { Input } from '../../../ui/Input';
import { AgreementEditDrawer } from '../Drawer/AgreementEditDrawer';
import { getAgreementCounterparty } from '../utils';

export interface AgreementSelectorProps {
  /** Currently selected agreement id. */
  value?: string | null;
  /** Called whenever the selection changes (string id, or `null` when cleared). */
  onChange: (next: string | null) => void;
  /** When set, the dropdown only shows agreements scoped to this counterparty. */
  counterpartyId?: string;
  counterpartyName?: string;
  allowCreateNew?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Combobox-style selector backed by `useAgreementListApi`. When
 * `counterpartyId` is provided, the dropdown is filtered client-side to
 * agreements whose other party matches — and the inline "create new" CTA
 * opens `AgreementEditDrawer` with that counterparty pre-filled + locked.
 */
export function AgreementSelector({
  value,
  onChange,
  counterpartyId,
  counterpartyName,
  allowCreateNew = true,
  disabled,
  placeholder,
  className,
}: AgreementSelectorProps) {
  const { t } = useTranslation(['m-agreement', 'c-main']);
  const { orgId } = useCurrentUser();

  const { data: agreements } = useAgreementListApi({});

  const scoped = useMemo(() => {
    if (!counterpartyId) return agreements;
    return agreements.filter(a => {
      const cp = getAgreementCounterparty(a, orgId);
      return cp && String(cp._id) === counterpartyId;
    });
  }, [agreements, counterpartyId, orgId]);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return scoped;
    return scoped.filter(a => {
      const cp = getAgreementCounterparty(a, orgId);
      const hay = [cp?.name ?? '', a.type, a.status].join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [scoped, search, orgId]);

  const selected = useMemo(
    () => agreements.find(a => String(a._id) === value) ?? null,
    [agreements, value],
  );

  const handleSelect = (a: Agreement) => {
    onChange(String(a._id));
    setIsOpen(false);
    setSearch('');
  };

  const handleCreated = (created: Agreement) => {
    onChange(String(created._id));
    setIsCreateOpen(false);
    setIsOpen(false);
    setSearch('');
  };

  const selectedCounterpartyName =
    selected ? getAgreementCounterparty(selected, orgId)?.name ?? '' : '';

  return (
    <div className={cn('space-y-0', className)}>
      <div className="flex items-stretch gap-1.5">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={isOpen}
              disabled={disabled}
              className={cn(
                'flex-1 justify-between gap-2 px-3 text-start font-normal h-10',
                !selected && 'text-muted-foreground',
              )}>
              {selected ? (
                <span className="flex items-center gap-2 min-w-0">
                  <FileTextIcon className="size-4 flex-shrink-0 text-gray-400" />
                  <span className="truncate">{selectedCounterpartyName}</span>
                </span>
              ) : (
                <span className="flex items-center gap-2 min-w-0">
                  <FileTextIcon className="size-4 flex-shrink-0 text-gray-300" />
                  <span className="truncate">
                    {placeholder ??
                      t('selector.placeholder', {
                        defaultValue: 'Pick an agreement (optional)',
                      })}
                  </span>
                </span>
              )}
              <ChevronsUpDown className="size-4 opacity-50 flex-shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]">
            <div className="flex items-center gap-2 border-b px-3 py-2 bg-white">
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('selector.search_placeholder', {
                  defaultValue: 'Search agreements…',
                })}
                className="h-8 border-0 px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                autoFocus
              />
              {allowCreateNew && filtered.length > 0 && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-8 flex-shrink-0 px-3"
                  onClick={() => {
                    setIsOpen(false);
                    setIsCreateOpen(true);
                  }}>
                  <Plus className="size-4 me-1" />
                  {t('c-main:crud_buttons.create_new', { defaultValue: 'Create new' })}
                </Button>
              )}
            </div>

            <Command shouldFilter={false} className="border-none rounded-none">
              <CommandList className="max-h-[260px]">
                {filtered.length === 0 ? (
                  <CommandEmpty className="px-4 py-6">
                    <div className="flex flex-col items-center gap-3 text-center">
                      <div className="size-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <FileTextIcon className="size-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600">
                        {counterpartyId
                          ? t('selector.empty_for_counterparty', {
                              defaultValue: 'No agreement yet with this counterparty.',
                            })
                          : t('selector.empty', {
                              defaultValue: 'No agreements match your search.',
                            })}
                      </p>
                      {allowCreateNew && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setIsOpen(false);
                            setIsCreateOpen(true);
                          }}>
                          <Plus className="size-4 me-1" />
                          {t('selector.create_for_counterparty', {
                            defaultValue: 'Create new agreement',
                          })}
                        </Button>
                      )}
                    </div>
                  </CommandEmpty>
                ) : (
                  <CommandGroup>
                    {filtered.map(a => {
                      const isSelected = String(a._id) === value;
                      const cpName =
                        getAgreementCounterparty(a, orgId)?.name ?? '';
                      return (
                        <CommandItem
                          key={String(a._id)}
                          value={String(a._id)}
                          onMouseDown={e => e.preventDefault()}
                          onSelect={() => handleSelect(a)}
                          className={cn(
                            'flex items-start gap-3 px-3 py-2 cursor-pointer',
                            isSelected && 'bg-gray-100',
                          )}>
                          <div className="mt-0.5 flex size-7 items-center justify-center rounded-full bg-primary/10 text-primary flex-shrink-0">
                            <FileTextIcon className="size-3.5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {cpName}
                            </div>
                            <div className="text-[11px] text-gray-500 truncate">
                              {t(`type.${a.type}`, { defaultValue: a.type })} ·{' '}
                              {t(`status.${a.status}`, { defaultValue: a.status })}
                            </div>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {selected && !disabled && (
          <Button
            type="button"
            variant="outline"
            className="px-2"
            onClick={() => onChange(null)}
            aria-label={t('selector.clear', { defaultValue: 'Clear agreement' })}>
            <X className="size-4" />
          </Button>
        )}
      </div>

      <AgreementEditDrawer
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        defaults={
          counterpartyId
            ? { counterpartyId, counterpartyName }
            : undefined
        }
        lockedFields={counterpartyId ? ['counterparty'] : undefined}
        onAgreementSaved={handleCreated}
      />
    </div>
  );
}
